// ============================================================================
// Hiring Kanban position card (Antonela spec §4.2, 2026-05-28).
//
// One card = one SEAT (item 621, Ben 2026-09-22). A request for three people
// is three cards; a card in a group reads "2 of 3", counted live from its
// siblings (seats.ts). A request on its own still reads "1 HIRE". Layout:
//
//   ● ROLE TITLE                          2 OF 3  |  1 HIRE
//     Hospital / Location
//     Shift: {shift}            (only if shift present)
//     Source: {posting_source}  (only if posting_source present)
//     17d ago                          [HIGH badge]
//
// Priority dot + badge: HIGH / MEDIUM / LOW reuse the RAG triad.
// Values live in tcs.css — do not re-state the hexes here (CW-J 0.5).
// Notes are revealed on hover (native title) and on the expand caret.
// ============================================================================

import { useState } from 'react';
import { StickyNote } from 'lucide-react';
import type { HiringRequestRow } from './hooks/use-hiring-data';
import type { HiringUrgency } from '../../shared/types';
import { RAG_COLORS } from '../../shared/design-tokens';
import { seatLabel } from './seats';

interface Props {
  row: HiringRequestRow;
  onClick: (row: HiringRequestRow) => void;
}

export default function RequestCard({ row, onClick }: Props) {
  const [expanded, setExpanded] = useState(false);
  const urgency = (row.urgency_label ??
    deriveLabel(row.urgency)) as HiringUrgency | null;
  const dotColor = urgency ? urgencyColor(urgency) : RAG_COLORS.Amber;
  const subline =
    row.account_name?.trim() ||
    row.facility_name?.trim() ||
    row.account_id ||
    '—';
  const age = daysAgo(row.created_date ?? row.created_at);
  const hasNotes = !!row.notes?.trim();

  return (
    <li>
      <div
        className="w-full rounded-lg border border-tcs-border bg-tcs-raised p-3 text-left shadow-sm transition hover:border-tcs-border-strong hover:shadow"
        title={hasNotes ? row.notes ?? undefined : undefined}
      >
        <button
          type="button"
          onClick={() => onClick(row)}
          className="block w-full text-left"
        >
          <div className="flex items-start gap-2">
            <span
              className="mt-1 inline-block h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: dotColor }}
              aria-label={`Priority ${urgency ?? 'unknown'}`}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="truncate text-sm font-medium text-tcs-text">
                  {row.title}
                </h3>
                <span className="shrink-0 rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tcs-text-secondary">
                  {seatLabel(row)}
                </span>
              </div>
              <div className="mt-0.5 truncate text-xs text-tcs-text-muted">
                {subline}
              </div>
              {row.shift?.trim() && (
                <div className="mt-0.5 truncate text-[11px] text-tcs-text-muted">
                  <span className="text-tcs-text-muted">Shift:</span>{' '}
                  {row.shift}
                </div>
              )}
              {row.posting_source?.trim() && (
                <div className="mt-0.5 truncate text-[11px] text-tcs-text-muted">
                  <span className="text-tcs-text-muted">Source:</span>{' '}
                  {row.posting_source}
                </div>
              )}
              <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-tcs-text-muted">
                <span>{age}</span>
                {urgency && <UrgencyPill urgency={urgency} />}
              </div>
            </div>
          </div>
        </button>

        {hasNotes && (
          <div className="mt-2 border-t border-tcs-border pt-1.5">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 text-[11px] text-tcs-text-muted hover:text-tcs-text-secondary"
            >
              <StickyNote className="h-3 w-3" />
              {expanded ? 'Hide notes' : 'Notes'}
            </button>
            {expanded && (
              <p className="mt-1 whitespace-pre-line text-[11px] text-tcs-text-secondary">
                {row.notes}
              </p>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function urgencyColor(u: HiringUrgency): string {
  if (u === 'High') return RAG_COLORS.Red;
  if (u === 'Low') return RAG_COLORS.Green;
  return RAG_COLORS.Amber; // Medium
}

function UrgencyPill({ urgency }: { urgency: HiringUrgency }) {
  const styles: Record<HiringUrgency, string> = {
    High: 'bg-st-danger-bg-strong text-st-danger-fg-strong',
    Medium: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
    Low: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
  };
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${styles[urgency]}`}
    >
      {urgency}
    </span>
  );
}

// Legacy rows may carry only the free-text composite ("High (5)").
function deriveLabel(raw: string | null): HiringUrgency | null {
  if (!raw) return null;
  const head = raw.trim().split(/\s/)[0];
  if (head === 'High' || head === 'Medium' || head === 'Low') return head;
  return null;
}

// "17d ago" style age. Accepts a DATE (YYYY-MM-DD) or ISO timestamp.
function daysAgo(value: string | null): string {
  if (!value) return '';
  const t = Date.parse(value);
  if (!Number.isFinite(t)) return '';
  const days = Math.floor((Date.now() - t) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return '1d ago';
  return `${days}d ago`;
}
